'use client';

import dynamic from 'next/dynamic';
import { useEffect, useMemo, useState, type JSX } from 'react';

import { fetchEventForecastHistory, fetchMarketForecastHistory, fetchSingleSeriesVo } from '@/app-migration/api/api';
import { DateTime } from 'luxon';
import { HistoricalDataPeriodInterval, MarketForecastHistoryVo } from 'models';

import KalshiLogo from '@components/DesignSystem/Icons/adhoc/KalshiLogo';
import EventPriceHistoryHeader from '@components/Graph/headers/EventPriceHistoryHeader';
import MarketPriceHistoryHeader from '@components/Graph/headers/MarketPriceHistoryHeader';
import NumericForecastHeader from '@components/Graph/headers/NumericForecastHeader';
import { GraphInteractionProvider } from '@components/Graph/interaction/GraphInteractionContext';
import Headline from 'DesignSystem3/Text/Headline';
import { Palette } from '@design/Theme';
import { useLiveEventMarketsForEvent } from 'hooks/useLiveMarketEntity';
import { useHasAnyLiveMarketQuote } from 'hooks/useLiveMarketQuotes';
import { useAuthStore } from 'lib/stores/authStore';
import { pinEmbedPalette } from '../../../../lib/embedPalette';
import { useFeatureFlagStore } from 'lib/stores/featureFlagStore';
import { EventVo } from '@models/event';
import { useMarketsNonExtrapolatedStatsHistory } from 'queries/eventForecastHistory';
import {
    eventExpirationValue,
    eventIsParsableNumeric,
    eventMarkets,
    eventSmoothedHistoricalMeans,
} from 'queries/marketEntityTransform';
import { useHydrateEventsIntoStore, useHydrateSeriesIntoStore, usePollEventIntoStore } from 'queries/markets';
import { shouldPollEventFast } from 'react_pages/Event/hooks/eventPolling';
import { useMarketEntitiesStore } from 'stores/zustand/marketEntitiesStore';
import { getHistoricalDataParams, mapForecastsHistory } from '@utils/charting';
import { PERIOD_TYPE } from '@constants/charting';
import { getSeriesPathDescription } from '@utils/series';

// Import chart components with no SSR to prevent hydration issues
const EventNumericHistoryGraph = dynamic(
    () => import('@components/Graph/EventNumericForecastGraph/EventNumericHistoryGraph'),
    {
        ssr: false,
        loading: () => <div />,
    },
);
const EventPriceHistoriesGraph = dynamic(
    () => import('@components/Graph/EventPriceHistoriesGraph/EventPriceHistoriesGraph'),
    {
        ssr: false,
        loading: () => <div />,
    },
);
const MarketPriceHistoryGraph = dynamic(
    () => import('@components/Graph/MarketPriceHistoryGraph/MarketPriceHistoryGraph'),
    {
        ssr: false,
        loading: () => <div />,
    },
);

// Client-only chart component to avoid hydration issues
const ClientOnlyCharts = dynamic(
    () =>
        Promise.resolve(function ClientOnlyChartsComponent(props: any) {
            const {
                eventEntity,
                rawMarketEntities,
                seriesEntity,
                event,
                totalReturnInDollars,
                customDescription,
                hideDateSelector,
                fixedHeight,
                fallbackImageSrc,
                vibrant,
                uiHidden,
                isCompact,
                activePeriod,
                setActivePeriod,
                forecastHistory,
                historicalForecasts,
                highlightedMarketHistoricalForecast,
            } = props;

            const isSingle = eventEntity
                ? rawMarketEntities.length === 1
                : event.markets.filter((m: any) => m.status !== 'invalid').length === 1;
            const isNumeric = eventEntity ? eventIsParsableNumeric(rawMarketEntities) : isServerSideNumeric(event);

            const eventSummary = {
                ticker: event.ticker,
                seriesTicker: event.series_ticker,
                openDate: new Date(Math.min(...event.markets.map((m: any) => new Date(m.open_date).getTime()))),
                volume: event.markets.map((m: any) => m.volume).reduce((a: number, b: number) => a + b, 0),
                listDate: event.markets[0]?.list_date || new Date().toISOString(),
            };

            const headerHeight = 64;
            // Use fixedHeight if provided, otherwise use window.innerHeight for iframe/responsive layouts
            const containerHeight =
                typeof props.fixedHeight === 'number' && !isNaN(props.fixedHeight)
                    ? props.fixedHeight
                    : typeof window !== 'undefined'
                      ? window.innerHeight
                      : 400;
            const chartHeight = Math.max(120, containerHeight - headerHeight);

            // Wrap each chart branch in a GraphInteractionProvider so the
            // body's published payload (projection / scrub state) reaches the
            // sibling header that used to live inside the chart subtree.
            if (isSingle) {
                // Single market: use MarketPriceHistoryGraph
                const marketVo = event.markets[0];
                const marketSummary = {
                    ticker: marketVo.ticker_name,
                    lastPrice: marketVo.last_price,
                    openDate: marketVo.open_date,
                    volume: marketVo.volume,
                };
                return (
                    <GraphInteractionProvider
                        eventSeriesTicker={eventEntity?.seriesTicker ?? event.series_ticker}
                        eventTargetSubtitle={rawMarketEntities[0]?.subtitle}
                        eventExpirationValue={eventEntity ? eventExpirationValue(rawMarketEntities) : ''}
                        totalReturnInDollars={totalReturnInDollars}
                        chartHidden={false}
                    >
                        <MarketPriceHistoryHeader />
                        <div style={{ height: chartHeight }}>
                            <MarketPriceHistoryGraph
                                useCents={false}
                                activePeriod={activePeriod}
                                eventTicker={event.ticker}
                                market={marketSummary}
                                hideDateSelector={hideDateSelector}
                                edgeToEdge={false}
                                fallbackImageSrc={fallbackImageSrc}
                                vibrant={vibrant}
                                uiHidden={uiHidden}
                                isCompact={isCompact}
                                showGridLines
                                blockAi={true}
                                setActivePeriod={setActivePeriod}
                                historicalForecasts={historicalForecasts}
                            />
                        </div>
                    </GraphInteractionProvider>
                );
            } else if (isNumeric) {
                // Multiple markets, numeric: use EventNumericHistoryGraph
                // `event` is required (non-optional) by this chart and immediately dereferences
                // `.ticker`/`.seriesTicker` — `eventEntity` is still undefined on the first client
                // render (useHydrateEventsIntoStore only writes it into the store from an effect
                // once the fetch resolves), so render nothing until it's ready rather than crashing.
                if (!eventEntity) return <div />;
                return (
                    <GraphInteractionProvider
                        eventSeriesTicker={eventEntity?.seriesTicker ?? event.series_ticker}
                        eventTargetSubtitle={rawMarketEntities[0]?.subtitle}
                        eventExpirationValue={eventEntity ? eventExpirationValue(rawMarketEntities) : ''}
                        totalReturnInDollars={totalReturnInDollars}
                        chartHidden={false}
                    >
                        <NumericForecastHeader />
                        <div style={{ height: chartHeight }}>
                            <EventNumericHistoryGraph
                                isExternalWidget={true}
                                event={eventEntity}
                                markets={rawMarketEntities}
                                series={seriesEntity}
                                historicalForecasts={historicalForecasts}
                                activePeriod={activePeriod}
                                setActivePeriod={setActivePeriod}
                                edgeToEdge={false}
                                fallbackImageSrc={fallbackImageSrc}
                                hideDateSelector={hideDateSelector}
                                uiHidden={uiHidden}
                                isCompact={isCompact}
                                blockAi={true}
                            />
                        </div>
                    </GraphInteractionProvider>
                );
            } else {
                // Multiple markets, categorical: use EventPriceHistoriesGraph (ignore selection for LARGE charts)
                // Same "event is required, undefined until hydration" guard as the isNumeric branch above.
                if (!eventEntity) return <div />;
                return (
                    <GraphInteractionProvider
                        eventSeriesTicker={eventEntity?.seriesTicker ?? event.series_ticker}
                        eventTargetSubtitle={rawMarketEntities[0]?.subtitle}
                        eventExpirationValue={eventEntity ? eventExpirationValue(rawMarketEntities) : ''}
                        totalReturnInDollars={totalReturnInDollars}
                        chartHidden={false}
                    >
                        <EventPriceHistoryHeader />
                        <div style={{ height: chartHeight }}>
                            <EventPriceHistoriesGraph
                                event={eventEntity}
                                markets={rawMarketEntities}
                                series={seriesEntity}
                                highlightedMarkets={undefined}
                                hideDateSelector={hideDateSelector}
                                edgeToEdge={false}
                                fallbackImageSrc={fallbackImageSrc}
                                vibrant={vibrant}
                                uiHidden={uiHidden}
                                isCompact={isCompact}
                                showGridLines
                                blockAi={true}
                                defaultPeriod={activePeriod}
                            />
                        </div>
                    </GraphInteractionProvider>
                );
            }
        }),
    {
        ssr: false,
        loading: () => <div></div>,
    },
);

// Helper function to check if an event has only one market
const isSingleMarket = (event: EventVo): boolean => {
    const nonHiddenMarkets = event.markets.filter((m) => m.status !== 'invalid');
    return nonHiddenMarkets.length === 1;
};

// Helper function to determine if an event is parsable numeric from server-side data
const isServerSideNumeric = (event: EventVo): boolean => {
    const nonHiddenMarkets = event.markets.filter((m) => m.status !== 'invalid');
    if (nonHiddenMarkets.length <= 1) {
        return false;
    }

    // Check if all markets have numeric targets
    return nonHiddenMarkets.every((market) => {
        // Check if the market sub-title or title contains numeric patterns
        const titleText = `${market.yes_sub_title || ''} ${market.title || ''} ${market.sub_title || ''}`;

        // Look for numeric patterns in titles (e.g., "At least 4%", "Above $1000", "More than 50")
        const numericPatterns = [
            /\d+\.?\d*%/, // Percentages: 4%, 3.5%
            /\$\d+\.?\d*/, // Dollar amounts: $1000, $50.25
            /\d+\.?\d*\s*(points?|percent|basis|bps)/i, // Points/percent: 100 points, 5 percent
            /(at least|above|below|more than|less than|over|under)\s*\d+/i, // Threshold language
            /\d+\.?\d*\s*(or more|or less|and above|and below)/i, // Range language
        ];

        const hasNumericPattern = numericPatterns.some((pattern) => pattern.test(titleText));

        // Also check custom_strike for explicit numeric fields
        if (market.custom_strike) {
            const customStrike = market.custom_strike as any;
            const numericFields = ['price', 'value', 'number', 'amount', 'count', 'score', 'rating', 'threshold'];
            const hasNumericStrike = numericFields.some(
                (field) => customStrike[field] !== undefined && !isNaN(Number(customStrike[field])),
            );

            if (hasNumericStrike) {
                return true;
            }
        }

        return hasNumericPattern;
    });
};

interface StateManagerGraphProps {
    seriesTicker: string;
    totalReturnInDollars?: number;
    event: EventVo;
    closeDate: Date;
    openDate: Date;
    utm_postfix: string;
    eventForecastHistory: MarketForecastHistoryVo[];
    customDescription?: string;
    highlightedMarketTicker?: string;
    highlightedMarketHistoricalForecast?: MarketForecastHistoryVo[];
    onPositionClick?: () => void;
    onIdeasClick?: () => void;
    withLogo?: boolean;
    hideDateSelector?: boolean;
    fixedHeight?: number;
    edgeToEdge?: boolean;
    fallbackImageSrc?: string;
    vibrant?: boolean;
    uiHidden?: boolean;
    isCompact?: boolean;
    useDarkMode?: boolean;
    initialPeriod?: PERIOD_TYPE;
}

const StateManagerGraph = (props: StateManagerGraphProps): JSX.Element => {
    const [forecastHistory, setForecastHistory] = useState<MarketForecastHistoryVo[]>(props.eventForecastHistory);
    const [activePeriod, setActivePeriod] = useState<PERIOD_TYPE>(props.initialPeriod ?? 'all');
    const [seriesTitle, setSeriesTitle] = useState<string>('');

    const eventTicker = props.event.ticker;
    useHydrateEventsIntoStore([eventTicker]);
    const eventEntity = useMarketEntitiesStore((s) => s.events[eventTicker]);
    const marketTickersForEvent = eventEntity?.marketTickers ?? [];
    // `marketEntitiesStore` is REST-owned, so subscribe here and overlay live data before
    // deriving the chart's visible markets.
    const rawMarketEntities = useLiveEventMarketsForEvent(eventTicker);
    const marketsToRemove = useFeatureFlagStore((s) => s.marketsToRemove) ?? [];
    // Drops markets hidden by the marketsToRemove kill-switch (or a hidden series) — the
    // removed MobX `Event.isParsableNumeric`/`.markets` getters filtered these internally, so
    // a kill-switched market must not be allowed to influence the numeric/single-market chart
    // classification or render in the chart itself.
    const visibleMarketEntities = eventEntity ? eventMarkets(rawMarketEntities, eventEntity, marketsToRemove) : [];

    useHydrateSeriesIntoStore(eventEntity ? [eventEntity.seriesTicker] : []);
    const seriesEntity = useMarketEntitiesStore((s) => (eventEntity ? s.series[eventEntity.seriesTicker] : undefined));

    const domainEventLoaded = !!eventEntity && rawMarketEntities.length === marketTickersForEvent.length;
    const isParsableNumeric = domainEventLoaded
        ? eventIsParsableNumeric(visibleMarketEntities)
        : isServerSideNumeric(props.event);

    // Fetch series title for proper URL generation
    useEffect(() => {
        fetchSingleSeriesVo(props.event.series_ticker).then((seriesResult) => {
            const seriesVo = Array.isArray(seriesResult) ? seriesResult[0] : seriesResult;
            if (seriesVo?.title) {
                setSeriesTitle(seriesVo.title);
            }
        });
    }, [props.event.series_ticker]);

    const goToMarket = () => {
        // Build proper URL: /markets/{seriesTicker}/{seriesPathDescription}/{eventTicker}
        const seriesPathDescription = seriesTitle ? getSeriesPathDescription(seriesTitle) : '';
        const seriesTickerLower = props.event.series_ticker.toLowerCase();
        const eventTickerLower = props.event.ticker.toLowerCase();

        const externalUrl = seriesPathDescription
            ? `https://kalshi.com/markets/${seriesTickerLower}/${seriesPathDescription}/${eventTickerLower}${props.utm_postfix}`
            : `https://kalshi.com/markets/${seriesTickerLower}${props.utm_postfix}`;
        window.open(externalUrl, '_blank');
    };

    const historicalForecasts = mapForecastsHistory(forecastHistory);
    const highlightedMarketHistoricalForecast = !!props.highlightedMarketHistoricalForecast
        ? mapForecastsHistory(props.highlightedMarketHistoricalForecast)
        : undefined;

    // Keep the REST entity fresh for metadata and previous-period baselines. Members poll fast
    // until this widget's live subscription delivers a quote; anonymous sessions stay on the
    // normal event cadence.
    const isLoggedInMember = useAuthStore((s) => s.isLoggedInMember);
    const eventMarketTickers = useMemo(() => rawMarketEntities.map((market) => market.ticker), [rawMarketEntities]);
    const hasAnyLiveQuote = useHasAnyLiveMarketQuote(eventMarketTickers);
    usePollEventIntoStore(eventTicker, {
        fast: shouldPollEventFast(isLoggedInMember, hasAnyLiveQuote),
        enabled: !!eventTicker,
    });

    // Pinned in both directions so the embedder's choice survives the colorPalette bridge
    // re-applying the visitor's own theme; see lib/embedPalette.
    useEffect(() => {
        pinEmbedPalette(props.useDarkMode ? 'dark' : 'light');
    }, [props.useDarkMode]);

    // For numeric events, fetch event-level forecast history with candlesticks fallback
    useEffect(() => {
        if (!isParsableNumeric) return;

        const { startTs, endTs, periodInterval } = getHistoricalDataParams(
            activePeriod,
            props.openDate.toISOString(),
            props.closeDate.toISOString(),
        );

        fetchEventForecastHistory(props.event.series_ticker, props.event.ticker, startTs, endTs, periodInterval).then(
            (eventHistory) => {
                if (eventHistory && eventHistory.length > 0) {
                    setForecastHistory(eventHistory);
                }
                // Empty result: the smoothedHistoricalMeans fallback effect below (fed by
                // useMarketsNonExtrapolatedStatsHistory's RQ-driven candlestick fetch) picks up
                // once all markets' candlestick history has loaded.
            },
        );
    }, [activePeriod, isParsableNumeric, domainEventLoaded, props.event]);

    // For binary/single-market events, fetch market-level forecast history
    useEffect(() => {
        if (isParsableNumeric) return; // Handled by numeric events useEffect above

        const nonHiddenMarkets = props.event.markets.filter((m) => m.status !== 'invalid');
        if (nonHiddenMarkets.length === 0) return;

        const { startTs, endTs, periodInterval } = getHistoricalDataParams(
            activePeriod,
            props.openDate.toISOString(),
            props.closeDate.toISOString(),
        );

        // For single market, fetch that market's history; for multi-market categorical, fetch first market
        const targetMarket = nonHiddenMarkets[0];
        fetchMarketForecastHistory(props.event.series_ticker, targetMarket.id, startTs, endTs, periodInterval).then(
            (marketHistory) => {
                if (marketHistory && marketHistory.length > 0) {
                    setForecastHistory(marketHistory);
                }
            },
        );
    }, [activePeriod, isParsableNumeric, domainEventLoaded, props.event]);

    // Fallback: use smoothedHistoricalMeans from candlesticks when event forecast history is empty.
    // Replaces domainEvent.markets.forEach(m => m.startPollingCandlesticksHistory(...)) — the RQ
    // hook below fetches each market's candlestick history and reports once all have loaded.
    const { statsHistoryByTicker, allCandlesticksLoaded } = useMarketsNonExtrapolatedStatsHistory(
        props.event.series_ticker,
        visibleMarketEntities,
        props.openDate.toISOString(),
        props.closeDate.toISOString(),
        activePeriod,
        { enabled: isParsableNumeric },
    );

    useEffect(() => {
        if (!domainEventLoaded || !isParsableNumeric) return;
        if (forecastHistory.length > 0) return; // Already have data
        if (!allCandlesticksLoaded) return;

        const [times, means] = eventSmoothedHistoricalMeans(
            visibleMarketEntities,
            statsHistoryByTicker,
            allCandlesticksLoaded,
            400,
        );
        if (times.length > 0) {
            const newHistoricalForecasts: MarketForecastHistoryVo[] = times.map((time, index) => ({
                event_ticker: props.event.ticker,
                market_ticker: '',
                numerical_forecast: means[index],
                raw_numerical_forecast: means[index],
                formatted_forecast: means[index].toString(),
                end_period_ts: time.getTime() / 1000,
                period_interval: HistoricalDataPeriodInterval.DAILY,
            }));
            setForecastHistory(newHistoricalForecasts);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domainEventLoaded, isParsableNumeric, forecastHistory.length, allCandlesticksLoaded, statsHistoryByTicker]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '4px',
                // The chart reserves space internally on the right (y-axis) and bottom
                // (x-axis), so bias the embed padding toward the left/bottom to even out
                // the visible margins. (Embed-only: this shifts the chart, it does not
                // touch the shared chart component.)
                paddingTop: '8px',
                paddingRight: '8px',
                paddingBottom: '12px',
                paddingLeft: '16px',
                height: '100%',
                backgroundColor: Palette.Surface.x10,
                cursor: 'pointer',
                overflow: 'hidden',
                width: '100%',
                boxSizing: 'border-box',
            }}
            onClick={goToMarket}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    flexGrow: 1,
                    backgroundColor: Palette.Surface.x10,
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        color: Palette.Text.x10,
                        width: '100%',
                        backgroundColor: Palette.Surface.x10,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '4px',
                            paddingLeft: '0px',
                            paddingRight: '16px',
                            paddingTop: '4px',
                            paddingBottom: '4px',
                            alignItems: 'center',
                            width: '100%',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Headline.x10
                            style={{
                                // Wrap long event titles instead of truncating with an ellipsis --
                                // partners' narrow embeds were shortening the headline. minWidth:0
                                // lets it wrap within the flex row; overflowWrap breaks long words.
                                minWidth: 0,
                                overflowWrap: 'anywhere',
                            }}
                        >
                            {props.event.title}
                        </Headline.x10>
                        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                            <KalshiLogo color={Palette.Brand.Primary} height={'18px'} />
                        </div>
                    </div>
                </div>
                <ClientOnlyCharts
                    eventEntity={eventEntity}
                    rawMarketEntities={visibleMarketEntities}
                    seriesEntity={seriesEntity}
                    event={props.event}
                    totalReturnInDollars={props.totalReturnInDollars}
                    customDescription={props.customDescription}
                    hideDateSelector={props.hideDateSelector}
                    fixedHeight={props.fixedHeight}
                    fallbackImageSrc={props.fallbackImageSrc}
                    vibrant={props.vibrant}
                    uiHidden={props.uiHidden}
                    isCompact={props.isCompact}
                    activePeriod={activePeriod}
                    setActivePeriod={setActivePeriod}
                    forecastHistory={forecastHistory}
                    historicalForecasts={historicalForecasts}
                    highlightedMarketHistoricalForecast={highlightedMarketHistoricalForecast}
                />
            </div>
        </div>
    );
};

export default StateManagerGraph;
